import { Component } from '@theme/component';

/**
 * `<bundle-reviews-widget>` — the REAL Judge.me review widget, refreshed per
 * product as the bundle selection changes.
 *
 * HOW A SWAP WORKS
 *   1. Fetch the widget HTML for ONE product id straight from Judge.me's cache
 *      server (the same GET its own preloader makes):
 *        {CACHE_SERVER_HOST}/widgets/shopify/{shop}?public_token=…&review_widget_product_ids={id}
 *      The response carries `review_widgets[id]` — the pre-rendered
 *      `.jdgm-rev-widg` markup. Cached in memory per id, so returning to a
 *      product is instant.
 *   2. Replace the placeholder NODE (not its innerHTML — Judge.me binds
 *      delegated handlers on the widget root, so a fresh node avoids stacking
 *      duplicate handlers on every swap).
 *   3. Hydrate by running the same setup steps `jdgm.initializeWidgets` runs on
 *      page load (header, summary/histogram, media gallery, pagination/sort
 *      bindings, review cards). Each step is optional — the form ones only exist
 *      once Judge.me lazy-loads its form module — so a missing one is skipped
 *      rather than aborting the whole hydrate, which is exactly what calling
 *      `initializeWidgets()` itself does on this theme.
 *
 * Previously this called `jdgmCacheServer.reloadAll()`, which is a full
 * Judge.me teardown: it removes every jdgm stylesheet/script/widget on the
 * page, re-fetches settings, and re-appends `loader.js` + CSS — on every
 * swatch tap. `reloadAll()` is kept ONLY as the fallback when the widget
 * library (`window.jdgm.$`) isn't present to hydrate with.
 *
 * WHEN A SWAP RUNS
 *   Only when the section is near the viewport. The reviews sit below the
 *   builder and the "As Seen On" marquee, so most selection changes happen with
 *   them off-screen; those just update `#pendingId` and the swap runs the moment
 *   the section scrolls near (IntersectionObserver, 600px margin).
 *
 * INITIAL RENDER
 *   The section server-renders the seed product's placeholder and the Judge.me
 *   app embed hydrates it as it does on any PDP — no work here. The widget
 *   preloader is also included as a plain `<script async>` in the section (so
 *   it starts downloading during HTML parse) for themes without the app embed.
 *
 * Refs: widget (the `.jdgm-review-widget[data-id]` placeholder).
 */
export class BundleReviewsWidgetComponent extends Component {
  requiredRefs = ['widget'];

  /** Product id currently rendered in the widget. */
  #loadedId = '';
  /** Product id the builder wants shown (may be ahead of #loadedId while off-screen). */
  #pendingId = '';
  /** @type {Map<string, string>} product id → `.jdgm-rev-widg` outerHTML */
  #cache = new Map();
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {number | undefined} */
  #debounceId;
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** True while the section is within the observer's margin of the viewport. */
  #near = false;
  /** @type {HTMLElement | null} the live placeholder node (replaced on each swap) */
  #widgetEl = null;

  connectedCallback() {
    super.connectedCallback();
    this.#widgetEl = /** @type {HTMLElement} */ (this.refs.widget);
    // The seed placeholder is a bare `.jdgm-review-widget[data-id]`. The Judge.me
    // app embed only gives it an (empty) header — the review list is fetched by
    // us, like every later product. It's the one fetch that doesn't wait for
    // the section to scroll near: it runs at idle so the list is there when the
    // shopper arrives, instead of a "0 reviews" header.
    this.#loadedId = '';
    this.#pendingId = String(this.dataset.seedId || this.#widgetEl?.dataset.id || '');

    this.#builder?.addEventListener('bundle:update', this.#onUpdate);
    this.#observeViewport();
    this.#builder?.requestUpdate?.();
    this.#loadSeedAtIdle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#debounceId);
    if (this.#seedFallbackId !== undefined) {
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(this.#seedFallbackId);
      clearTimeout(this.#seedFallbackId);
    }
    this.#abort?.abort();
    this.#io?.disconnect();
    this.#io = null;
    this.#builder?.removeEventListener('bundle:update', this.#onUpdate);
  }

  /** Builder sits above this section, so look it up globally. */
  get #builder() {
    return this.closest('bundle-builder') || document.querySelector('bundle-builder');
  }

  /**
   * Initial render: fetch whatever product is pending once the browser is
   * idle (or after 1.5s), regardless of scroll position. Later swaps are
   * viewport-gated; this one isn't, so the first thing the shopper sees on
   * scrolling down is a populated widget.
   */
  #loadSeedAtIdle() {
    const run = () => {
      this.#seedFallbackId = undefined;
      if (!this.#loadedId) this.#flush(true);
    };
    if ('requestIdleCallback' in window) {
      this.#seedFallbackId = window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      this.#seedFallbackId = window.setTimeout(run, 1500);
    }
  }

  /** @type {number | undefined} */
  #seedFallbackId;

  #observeViewport() {
    if (!('IntersectionObserver' in window)) {
      this.#near = true;
      return;
    }
    this.#io = new IntersectionObserver(
      (entries) => {
        this.#near = entries.some((e) => e.isIntersecting);
        if (this.#near) this.#flush();
      },
      { rootMargin: '600px 0px' }
    );
    this.#io.observe(this);
  }

  #onUpdate = () => {
    const builder = this.#builder;
    // @ts-ignore — getDraftSelection is defined on BundleBuilderComponent.
    const sel = builder?.getDraftSelection?.(builder.state?.currentStep);
    const id = sel?.product?.productId;
    if (id == null) return;
    const idStr = String(id);
    if (idStr === this.#pendingId) return;
    this.#pendingId = idStr;
    clearTimeout(this.#debounceId);
    this.#debounceId = window.setTimeout(() => this.#flush(), 200);
  };

  /**
   * Render #pendingId if it differs from what's shown and we're near the
   * viewport (`force` skips the viewport gate — used for the initial load).
   * @param {boolean} [force]
   */
  async #flush(force = false) {
    const id = this.#pendingId;
    if (!id || id === this.#loadedId || (!force && !this.#near)) return;

    const cached = this.#cache.get(id);
    if (cached) {
      this.#swap(id, cached);
      return;
    }

    this.#abort?.abort();
    const controller = new AbortController();
    this.#abort = controller;
    this.toggleAttribute('data-loading', true);
    try {
      const html = await this.#fetchWidget(id, controller.signal);
      if (controller.signal.aborted) return;
      if (html) {
        this.#cache.set(id, html);
        if (id === this.#pendingId) this.#swap(id, html);
      } else {
        this.#reloadViaPreloader(id);
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.warn('[bb-reviews-widget] cache-server fetch failed, falling back to reloadAll', error);
      this.#reloadViaPreloader(id);
    } finally {
      if (this.#abort === controller) {
        this.#abort = null;
        this.removeAttribute('data-loading');
      }
    }
  }

  /**
   * One product's review widget markup from the Judge.me cache server.
   * @param {string} id
   * @param {AbortSignal} signal
   * @returns {Promise<string>}
   */
  async #fetchWidget(id, signal) {
    const w = /** @type {any} */ (window);
    const jdgm = w.jdgm || {};
    const shop = jdgm.SHOP_DOMAIN || this.dataset.shop || w.Shopify?.shop;
    if (!shop) return '';
    const host = jdgm.CACHE_SERVER_HOST || 'https://cache.judge.me';
    const params = new URLSearchParams({
      public_token: jdgm.PUBLIC_TOKEN || '',
      review_widget_product_ids: id,
    });
    const res = await fetch(`${host}/widgets/shopify/${shop}?${params}`, { signal });
    if (!res.ok) throw new Error(`cache server ${res.status}`);
    const json = await res.json();
    return json?.review_widgets?.[id] || '';
  }

  /**
   * Replace the placeholder with a fresh node holding `html`, then hydrate.
   * The container keeps its previous height until the new widget has laid out,
   * so the page doesn't collapse and re-expand under the shopper.
   * @param {string} id
   * @param {string} html
   */
  #swap(id, html) {
    const old = this.#widgetEl;
    if (!old) return;
    const prevHeight = this.offsetHeight;
    this.style.minBlockSize = `${prevHeight}px`;

    const next = document.createElement('div');
    next.className = 'jdgm-widget jdgm-review-widget';
    next.setAttribute('ref', 'widget');
    next.dataset.id = id;
    next.dataset.productId = id;
    next.innerHTML = html;
    old.replaceWith(next);
    this.#widgetEl = next;
    this.#loadedId = id;

    if (!this.#hydrate(next)) {
      this.#reloadViaPreloader(id);
    }
    requestAnimationFrame(() => {
      this.style.minBlockSize = '';
    });
  }

  /**
   * Run Judge.me's own per-widget setup on `el`. Mirrors `jdgm.initializeWidgets`
   * step for step, but tolerates steps that aren't loaded yet.
   * @param {HTMLElement} el
   * @returns {boolean} false when the widget library isn't available
   */
  #hydrate(el) {
    const jdgm = /** @type {any} */ (window).jdgm;
    if (!jdgm || typeof jdgm.$ !== 'function') return false;
    const $el = jdgm.$(el);
    const steps = [
      '_addEmptyWidgetHeader',
      '_customizeReviewWidget',
      'setupMediaGallery',
      '_renderAndSetupReviewForm',
      '_setupQuestionsForm',
      '_setupLoadReviewsEventsFor',
      '_setupFormsSubmit',
    ];
    for (const name of steps) {
      if (typeof jdgm[name] !== 'function') continue;
      try {
        jdgm[name]($el);
      } catch (error) {
        console.warn(`[bb-reviews-widget] jdgm.${name} failed`, error);
      }
    }
    $el.addClass(jdgm.DONE_SETUP_WIDGET_CLASS || 'jdgm--done-setup-widget');
    // jdgm caches its widget list at boot; point it at the new node so its own
    // helpers (translation, subtabs, …) keep working after a swap.
    if (jdgm.caches) {
      jdgm.caches.$revWidgets = jdgm.$('.jdgm-review-widget, [data-widget="review"]');
      jdgm.caches.$widgetWithReviews = jdgm.$('.jdgm-review-widget, .jdgm-revs-tab, .jdgm-all-reviews-widget');
    }
    try {
      jdgm.customizeReviews?.();
    } catch (error) {
      console.warn('[bb-reviews-widget] jdgm.customizeReviews failed', error);
    }
    return true;
  }

  /**
   * Fallback: Judge.me's cache-server preloader re-fetches + hydrates every
   * `.jdgm-review-widget[data-id]` on the page. Heavy (full jdgm re-bootstrap),
   * so only used when the direct path can't run.
   * @param {string} id
   */
  async #reloadViaPreloader(id) {
    const el = this.#widgetEl;
    if (!el) return;
    await this.#ensurePreloader();
    const w = /** @type {any} */ (window);
    if (typeof w.jdgmCacheServer?.reloadAll !== 'function') return;
    el.dataset.id = id;
    el.dataset.productId = id;
    el.innerHTML = '';
    el.classList.remove('jdgm--done-setup-widget', 'jdgm--done-setup');
    this.#loadedId = id;
    try {
      w.jdgmCacheServer.reloadAll();
    } catch (error) {
      console.warn('[bb-reviews-widget] reloadAll failed', error);
    }
  }

  /**
   * Make sure `jdgmCacheServer` exists (the section ships the preloader as a
   * plain async script; this covers the case where it hasn't executed yet).
   */
  async #ensurePreloader() {
    const w = /** @type {any} */ (window);
    w.jdgm = w.jdgm || {};
    if (!w.jdgm.SHOP_DOMAIN && this.dataset.shop) w.jdgm.SHOP_DOMAIN = this.dataset.shop;
    if (!w.jdgm.PLATFORM) w.jdgm.PLATFORM = 'shopify';
    if (typeof w.jdgmCacheServer !== 'undefined') return;

    if (!document.querySelector('script[src*="widget_preloader.js"]')) {
      const s = document.createElement('script');
      s.src = 'https://cdnwidget.judge.me/widget_preloader.js';
      s.async = true;
      document.head.appendChild(s);
    }
    for (let i = 0; i < 50 && typeof w.jdgmCacheServer === 'undefined'; i++) {
      await new Promise((r) => setTimeout(r, 100));
    }
  }
}

if (!customElements.get('bundle-reviews-widget')) {
  customElements.define('bundle-reviews-widget', BundleReviewsWidgetComponent);
}
